import { Fruit } from './Fruit'
import { FruitType } from './FruitTypes'

export interface RendererConfig {
  canvas: HTMLCanvasElement
  boundaryLineY: number
}

export class Renderer {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  boundaryLineY: number
  width: number = 0
  height: number = 0

  constructor(config: RendererConfig) {
    this.canvas = config.canvas
    this.boundaryLineY = config.boundaryLineY

    const ctx = this.canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Could not get 2D context')
    }
    this.ctx = ctx

    this.resize()
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1

    this.width = rect.width
    this.height = rect.height

    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr

    this.ctx.scale(dpr, dpr)
  }

  clear(): void {
    this.ctx.fillStyle = '#16213e'
    this.ctx.fillRect(0, 0, this.width, this.height)
  }

  drawBoundaryLine(): void {
    this.ctx.strokeStyle = 'rgba(233, 69, 96, 0.5)'
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([10, 10])
    this.ctx.beginPath()
    this.ctx.moveTo(0, this.boundaryLineY)
    this.ctx.lineTo(this.width, this.boundaryLineY)
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }

  drawFruit(fruit: Fruit): void {
    const { x, y, radius, type } = fruit

    // Draw emoji
    const fontSize = radius * 2
    this.ctx.font = `${fontSize}px serif`
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText(type.emoji, x, y)
  }

  drawPreviewFruit(x: number, type: FruitType): void {
    const y = this.boundaryLineY - 20

    // Preview emoji at full opacity
    const fontSize = type.radius * 2
    this.ctx.font = `${fontSize}px serif`
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'
    this.ctx.fillText(type.emoji, x, y)

    // Drop indicator line
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'
    this.ctx.lineWidth = 1
    this.ctx.setLineDash([5, 5])
    this.ctx.beginPath()
    this.ctx.moveTo(x, y + type.radius)
    this.ctx.lineTo(x, this.height)
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }

  drawMergeEffect(x: number, y: number, radius: number): void {
    // Expanding ring effect
    this.ctx.beginPath()
    this.ctx.arc(x, y, radius * 1.5, 0, Math.PI * 2)
    this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
    this.ctx.lineWidth = 4
    this.ctx.stroke()
  }

  render(
    fruits: Fruit[],
    previewX: number | null,
    currentFruitType: FruitType | null,
    canDrop: boolean
  ): void {
    this.clear()
    this.drawBoundaryLine()

    // Draw all fruits
    for (const fruit of fruits) {
      if (!fruit.markedForRemoval) {
        this.drawFruit(fruit)
      }
    }

    // Draw preview
    if (previewX !== null && currentFruitType && canDrop) {
      this.drawPreviewFruit(previewX, currentFruitType)
    }
  }
}
